import React, { useState, useCallback, useRef } from 'react'
import './ripple.css'

interface RippleProps {
  children: React.ReactNode
  color?: string
  duration?: number
  className?: string
  disabled?: boolean
  /** 触发延迟时间（毫秒），用于区分点击和滚动，默认 80ms */
  delay?: number
  /** 滑动取消阈值（像素），延迟期间滑动超过此距离则取消水波，默认 10px */
  touchMoveThreshold?: number
}

interface RippleItem {
  x: number
  y: number
  size: number
  id: number
}

interface PendingRipple {
  x: number
  y: number
  size: number
  startX: number
  startY: number
  timerId: ReturnType<typeof setTimeout>
}

export default function Ripple({ 
  children, 
  color = 'rgba(0, 0, 0, 0.4)', 
  duration = 1000,
  className = '',
  disabled = false,
  delay = 100,
  touchMoveThreshold = 10
}: RippleProps) {
  const [ripples, setRipples] = useState<RippleItem[]>([])
  const pendingRippleRef = useRef<PendingRipple | null>(null)

  // 实际添加水波
  const createRipple = useCallback((x: number, y: number, size: number) => {
    const rippleId = Date.now() + Math.random()

    const newRipple: RippleItem = {
      x,
      y,
      size,
      id: rippleId
    }

    setRipples(prev => [...prev, newRipple])

    // 自动移除
    setTimeout(() => {
      setRipples(prev => prev.filter(r => r.id !== rippleId))
    }, duration)
  }, [duration])

  // touchstart: 延迟触发水波
  const handleTouchStart = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    if (disabled) return

    const target = event.currentTarget
    const rect = target.getBoundingClientRect()
    
    const touch = event.touches[0]
    const clientX = touch.clientX
    const clientY = touch.clientY

    const size = Math.max(rect.width, rect.height) * 2
    const x = clientX - rect.left - size / 2
    const y = clientY - rect.top - size / 2

    // 清除之前的 pending
    if (pendingRippleRef.current) {
      clearTimeout(pendingRippleRef.current.timerId)
    }

    // 延迟触发水波
    const timerId = setTimeout(() => {
      if (pendingRippleRef.current) {
        createRipple(x, y, size)
        pendingRippleRef.current = null
      }
    }, delay)

    pendingRippleRef.current = {
      x,
      y,
      size,
      startX: clientX,
      startY: clientY,
      timerId
    }
  }, [disabled, delay, createRipple])

  // touchmove: 检测滑动，超过阈值取消水波
  const handleTouchMove = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    const pending = pendingRippleRef.current
    if (!pending) return

    const touch = event.touches[0]
    const dx = touch.clientX - pending.startX
    const dy = touch.clientY - pending.startY
    const distance = Math.sqrt(dx * dx + dy * dy)

    // 超过阈值，取消水波触发
    if (distance > touchMoveThreshold) {
      clearTimeout(pending.timerId)
      pendingRippleRef.current = null
    }
  }, [touchMoveThreshold])

  // touchend: 如果还在延迟期间，立即触发水波
  const handleTouchEnd = useCallback(() => {
    const pending = pendingRippleRef.current
    if (pending) {
      clearTimeout(pending.timerId)
      createRipple(pending.x, pending.y, pending.size)
      pendingRippleRef.current = null
    }
  }, [createRipple])

  // touchcancel: 取消水波
  const handleTouchCancel = useCallback(() => {
    if (pendingRippleRef.current) {
      clearTimeout(pendingRippleRef.current.timerId)
      pendingRippleRef.current = null
    }
  }, [])

  return (
    <div 
      className={`ripple-container ${className}`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchCancel}
    >
      {children}
      {ripples.length > 0 && (
        <div className="ripple-wrapper">
          {ripples.map(ripple => (
            <span
              key={ripple.id}
              className="ripple-effect"
              style={{
                left: ripple.x,
                top: ripple.y,
                width: ripple.size,
                height: ripple.size,
                backgroundColor: color,
                animationDuration: `${duration}ms`
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}
